/**
 * Admin — Security alert queue.
 * Server-generated heuristics (failed-login bursts, bulk exports, role changes,
 * backup failures, wearable token failures) with an open/acknowledged/dismissed
 * lifecycle.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldAlert, CheckCircle2, XCircle } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";

const SEVERITY = {
  low: "text-zinc-400 border-zinc-500/40",
  medium: "text-amber-400 border-amber-500/40",
  high: "text-red-400 border-red-500/40",
};
const STATUS_CLS = {
  open: "text-red-400",
  acknowledged: "text-amber-400",
  dismissed: "text-zinc-500",
};

export default function AdminAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [busy, setBusy] = useState({});

  const refresh = async () => {
    setLoading(true);
    try { const { data } = await api.get(statusFilter ? `/security-alerts?status=${statusFilter}` : "/security-alerts"); setAlerts(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [statusFilter]);

  const resolve = async (id, status) => {
    setBusy({ ...busy, [id]: true });
    try {
      await api.patch(`/security-alerts/${id}`, { status, resolution_notes: null });
      toast.success(`Marked ${status}.`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy({ ...busy, [id]: false }); }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="fl-label text-[#f47a20]">Security</div>
        <h1 className="fl-heading text-3xl md:text-4xl mt-2">Security alerts</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Auto-generated heuristics from the audit trail. Investigate, then mark acknowledged or dismissed. Alerts are deduplicated per (kind, actor, IP) — the same underlying event won't create noise.
        </p>

        <div className="mt-6 flex items-center gap-3">
          <label className="fl-label">Status</label>
          <select className="fl-input max-w-[14rem]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="alerts-status-filter">
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="dismissed">Dismissed</option>
            <option value="">All</option>
          </select>
          <button onClick={refresh} className="fl-btn-ghost text-sm ml-auto" data-testid="alerts-refresh">
            {loading ? <Loader2 size={12} className="animate-spin"/> : "Refresh"}
          </button>
        </div>

        <div className="mt-4 space-y-2" data-testid="alerts-list">
          {!loading && alerts.length === 0 && (
            <div className="fl-card p-8 text-center text-sm text-zinc-500">No alerts.</div>
          )}
          {alerts.map((a) => (
            <div key={a.id} className={`fl-card p-4 border-l-4 ${SEVERITY[a.severity] || SEVERITY.low}`} data-testid={`alert-row-${a.id}`}>
              <div className="flex items-start gap-3">
                <ShieldAlert size={16} className={SEVERITY[a.severity]?.split(" ")[0]} />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-zinc-100">{a.title}</span>
                    <span className={`text-[10px] uppercase tracking-widest ${STATUS_CLS[a.status]}`}>{a.status}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{a.severity} · seen {a.count}×</span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    kind={a.kind} · latest {new Date(a.last_seen_at).toLocaleString()}
                  </div>
                  {a.latest_meta && (
                    <pre className="mt-2 text-[11px] text-zinc-400 bg-black/30 p-2 rounded overflow-x-auto">{JSON.stringify(a.latest_meta, null, 2)}</pre>
                  )}
                </div>
                {a.status === "open" && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => resolve(a.id, "acknowledged")} disabled={busy[a.id]} className="fl-btn-ghost text-xs" data-testid={`alert-ack-${a.id}`}>
                      {busy[a.id] ? <Loader2 size={12} className="animate-spin"/> : <><CheckCircle2 size={12}/> Acknowledge</>}
                    </button>
                    <button onClick={() => resolve(a.id, "dismissed")} disabled={busy[a.id]} className="fl-btn-ghost text-xs" data-testid={`alert-dismiss-${a.id}`}>
                      <XCircle size={12}/> Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
